// Trusted rich-block registry for generated views.
//
// The composer receives only these IDs and presentation rules. It never names
// Vue components, props, HTML, JavaScript, or URLs. The renderer maps IDs to
// trusted components in src/components/view/ViewRichBlock.vue.

import { places } from './places.js';
import { archiveProjectRichBlocks } from './archiveProjectSources.js';
import { slidePoiseRichBlocks } from './slidepoiseSources.js';
import { cathayFlexRichBlocks } from './cathayFlexSources.js';

const travelMedia = places.map((place) => {
  const filename = String(place.img || '').split('/').pop();
  return {
    src: filename ? `/assets/travel/previews/${filename}.webp` : place.img,
    fullSrc: place.img,
    alt: place.en,
  };
});

const placeId = (place) => String(place.en || '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

const travelPlaceVariants = places.map((place) => ({
  id: `interactive.travels.place.${placeId(place)}`,
  title: place.label,
  description: `Open Henry’s Travels globe with ${place.label} selected.`,
  componentDescription: `The Travels globe focused on ${place.label}, with its pin selected and location photograph open. Visitors can still rotate the globe or choose another place.`,
  narrativeGuidance: `Use this focused city state when ${place.en} itself contributes material evidence. Pair it with a personal, study, work, research, project, or reflection source when explaining what the place meant to Henry.`,
  anchor: placeId(place),
  topics: [place.en, place.label, place.local].filter(Boolean),
  media: travelMedia.filter((item) => item.alt === place.en),
  entity: {
    kind: 'travel-place',
    id: placeId(place),
    focus_place: place,
  },
}));

export const viewRichBlocks = [
  ...archiveProjectRichBlocks,
  ...slidePoiseRichBlocks,
  ...cathayFlexRichBlocks,
  {
    id: 'interactive.about.exploration-paths',
    kind: 'interactive',
    title: 'See Henry through your own lens',
    description: 'Choose Search, Ask, or View as a direct way to explore Henry’s original site material.',
    componentDescription: 'A working three-path entry point from Henry’s About page. Search opens precise source retrieval, Ask opens a cited conversation, and View opens AI composition around a visitor’s chosen angle.',
    narrativeGuidance: 'Use it when the visitor is asking how to explore Henry, how this website represents him, or how Search, Ask, and View differ. Introduce the three choices from the visitor’s goal and explain which path best fits what they want to understand.',
    activationTopics: ['explore Henry', 'personal representation', 'site navigation', 'Search Ask View'],
    route: '/about',
    anchor: 'begin-with-what-you-want-to-understand',
    topics: ['explore Henry', 'personal representation', 'AI interface', 'site navigation', 'Search', 'Ask', 'View'],
    allowedPresentation: 'interactive',
    component: 'AboutAiEntry'
  },
  {
    id: 'interactive.search.system-paths',
    retired: true, // Preserve historical Views while excluding this removed overview from new compositions.
    kind: 'interactive',
    title: 'Choose how you want to explore',
    description: 'Compare how Search, Ask, and View turn Henry’s original site material into three different experiences.',
    componentDescription: 'An interactive explanation of Henry’s site-wide AI project. Visitors can switch among Search, Ask, and View to see how the same material supports direct discovery, answers with citations, or a newly composed page.',
    narrativeGuidance: 'Use it when the visitor is asking how the site’s AI system works or how Search, Ask, and View differ technically. Explain what each path lets a visitor accomplish before introducing the mechanisms that support it. This differs from the About page entry, which helps a visitor choose a personal way to explore Henry.',
    activationTopics: ['site AI system', 'retrieval architecture', 'Search Ask View', 'how the site works'],
    route: '/aipersonalarchive',
    anchor: 'choose-how-you-want-to-explore',
    topics: ['AI system', 'retrieval architecture', 'Search', 'Ask', 'View', 'grounding', 'composition', 'site AI'],
    allowedPresentation: 'interactive',
    component: 'SiteAiPathExplorer'
  },
  {
    id: 'interactive.search.embedding-map',
    kind: 'interactive',
    title: 'Map of the site by meaning',
    description: 'Interactive map of semantically related passages across the site.',
    componentDescription: 'An interactive semantic map of this website. Visitors can explore how passages from different pages cluster by meaning and open the original material.',
    narrativeGuidance: 'Include it only when the visitor is asking how the site search or information architecture works. Explain the cluster or relationship they should inspect.',
    activationTopics: ['semantic map', 'embeddings', 'information architecture', 'how search works'],
    route: '/aipersonalarchive',
    anchor: 'shared-memory',
    topics: ['search', 'semantic map', 'embeddings', 'information architecture', 'site exploration'],
    allowedPresentation: 'interactive',
    component: 'EmbeddingMap'
  },
  {
    id: 'interactive.search.retrieval-lab',
    kind: 'interactive',
    title: 'Search retrieval lab',
    description: 'Compare lexical, semantic, and fused rankings and move the relevance threshold.',
    componentDescription: 'A working retrieval playground that compares lexical, semantic, and fused search rankings for the same query, with an adjustable relevance threshold.',
    narrativeGuidance: 'Use it as evidence about Henry’s search system. Name the ranking behavior the visitor can test and connect that behavior to the surrounding explanation.',
    activationTopics: ['search retrieval', 'ranking', 'relevance threshold', 'lexical semantic'],
    route: '/aipersonalarchive',
    anchor: 'retrieval-before-generation',
    topics: ['search', 'retrieval', 'ranking', 'relevance threshold', 'lexical', 'semantic'],
    allowedPresentation: 'interactive',
    component: 'RetrievalLab'
  },
  {
    id: 'interactive.search.view-composer',
    kind: 'interactive',
    title: 'See Henry through your own lens',
    description: 'Compose a View from a question, theme, or perspective, or explore Views that other visitors have shared.',
    componentDescription: 'A working entry point to Henry’s View system. Visitors can create a perspective from Henry’s original site material, keep every source visible, or browse Views that other visitors have shared.',
    narrativeGuidance: 'Use it when the visitor would benefit from composing their own perspective or seeing how other people have interpreted Henry’s archive. Explain that a View is a dated slice shaped around one angle and grounded in original site material. Keep this focused on creating and exploring Views, while the three-path systems component explains the underlying mechanism.',
    activationTopics: ['compose View', 'public Views', 'shared Views', 'personal perspective'],
    route: '/aipersonalarchive',
    anchor: 'compose-a-view',
    topics: ['compose a View', 'public Views', 'personal perspective', 'AI composition', 'original sources', 'shared Views'],
    allowedPresentation: 'interactive',
    component: 'ViewComposeInvitation'
  },
  {
    id: 'interactive.insurance.rule-engine',
    kind: 'interactive',
    title: 'Insurance configuration rule engine',
    description: 'Try the packing-rule analogue that mirrors the HSBC product-to-UI mapping engine.',
    componentDescription: 'A working analogue of Henry’s HSBC insurance configuration engine. The demo substitutes a trip specification for an insurance product specification, rule columns for business scenarios, and packing items for UI components, then uses the same decision-table logic to map inputs to outputs.',
    narrativeGuidance: 'Use it when the HSBC project, product configuration, mapping, or decision tables are directly relevant. First explain the real mapping from product specification through business scenarios to UI-component requirements. Then explain the trip-and-packing analogy before inviting the visitor to change the inputs.',
    activationTopics: ['rule engine', 'insurance configuration', 'decision tables', 'HSBC configuration'],
    route: '/playbook',
    anchor: 'try-it',
    topics: ['rule engine', 'configuration', 'decision tables', 'HSBC', 'product configuration'],
    allowedPresentation: 'interactive',
    component: 'InsuranceRuleEngine'
  },
  {
    id: 'interactive.rag.reference-linking',
    kind: 'interactive',
    title: 'Cross-page reference linking demo',
    description: 'Connect a cross-page note to the sentence it qualifies and see the resulting retrieval chunk.',
    componentDescription: 'An interactive demonstration from Henry’s PwC RAG ingestion project. It shows how a cross-page note is reattached before chunking and why the resulting retrieval unit contains both the rule and the qualification needed to interpret it.',
    narrativeGuidance: 'Use it when RAG ingestion, cross-page references, chunking, retrieval, or document structure matter to the answer. Explain the structural failure before linking, the ingestion repair, and what changes in the retrieved chunk afterward.',
    activationTopics: ['cross-page references', 'reference linking', 'document notes', 'complex document structure'],
    route: '/ragflow-pipeline',
    anchor: 'notes',
    topics: ['RAG', 'document structure', 'cross-page references', 'retrieval', 'PwC'],
    allowedPresentation: 'interactive',
    component: 'ReferenceLinkingDemo'
  },
  {
    id: 'interactive.document-review.guidance',
    kind: 'interactive',
    title: 'Guidance for long-document risk assessment',
    description: 'Explore how evidence requirements, optional source guidance and decision rules support report assessment.',
    componentDescription: 'Three columns explain the assessment guidance. Evidence requirements guide extraction. Optional source guidance identifies likely sections for further context. Decision rules guide synthesis after the evidence is collected.',
    narrativeGuidance: 'Explain how Henry translated risk definitions into evidence requirements and decision rules for a workflow that reads every page. Introduce source locations as optional guidance for revisiting the report when more context is needed. Use the component to explain how evidence gathering and judgement are guided at different stages.',
    activationTopics: ['risk assessment', 'evidence requirements', 'decision rules', 'AI architecture'],
    route: '/document-review',
    anchor: 'risk-assessment-guidance',
    topics: ['PwC', 'risk assessment', 'long documents', 'evidence', 'source guidance', 'decision rules'],
    allowedPresentation: 'interactive',
    component: 'RiskAssessmentGuide'
  },
  {
    id: 'interactive.document-review.architecture',
    kind: 'interactive',
    title: 'Long-document assessment workflow',
    description: 'Follow chunking, page rendering, evidence extraction, synthesis and presentation in a review workspace.',
    componentDescription: 'A five-step diagram explains the complete workflow. Chunking divides the report into reading tasks, pages become images, extraction builds an evidence ledger, synthesis applies decision rules, and the review interface presents findings beside source material. Selecting a step highlights it and displays its explanation.',
    narrativeGuidance: 'Introduce the complete assessment workflow before its individual design decisions. Explain how chunking and page rendering prepare the report, extraction collects evidence, synthesis applies decision rules and the review interface presents findings beside source material. Use the diagram to orient the reader before discussing the evidence schema or reviewer workflow.',
    activationTopics: ['long documents', 'evidence coverage', 'AI architecture', 'evidence synthesis'],
    route: '/document-review',
    anchor: 'document-review-architecture',
    topics: ['PwC', 'AI architecture', 'long documents', 'coverage', 'evidence', 'synthesis'],
    allowedPresentation: 'interactive',
    component: 'DocumentReviewArchitecture'
  },
  {
    id: 'interactive.it-ops.knowledge',
    kind: 'interactive',
    title: 'The knowledge behind a question-answering assistant',
    description: 'Explore knowledge gaps, review the research they guide, and monitor the original sources behind accepted knowledge.',
    componentDescription: 'An interactive knowledge workspace supporting the RAG question-answering assistant in Henry’s PwC AI for IT Operations project. A fixed two-by-two matrix crosses known and latent needs with covered and missing knowledge. Its four cases show answering a known question, acquiring knowledge for a real gap, applying existing knowledge to an unasked question, and using AI to predict a valuable uncovered question. The two missing-knowledge cases guide one research objective. Visitors see why the discovered sources contribute useful evidence. Expandable research notes explain the clustering, focused crawling and re-seeding behind that selection. Source monitoring revisits the original URLs, compares current content with accepted snapshots, and brings a proposed update to human review only after agent assessment.',
    narrativeGuidance: 'Explain Knowledge Management as the acquisition and maintenance of evidence for the platform’s RAG question-answering assistant. Invite readers to explore the research behind an unanswered question or run a check on an original source. The monitoring workflow compares source content and prepares a knowledge update when needed. Browser Control is a separate capability in the same platform.',
    activationTopics: ['knowledge management', 'RAG assistant', 'question answering', 'knowledge gaps', 'focused crawling', 'source registry', 'IT operations'],
    route: '/ai-it-operations',
    anchor: 'certificate-knowledge-journey',
    topics: ['PwC', 'IT operations', 'knowledge management', 'knowledge gaps', 'focused crawling', 'source registry', 'provenance'],
    allowedPresentation: 'interactive',
    component: 'ItOpsJourney'
  },
  {
    id: 'interactive.it-ops.browser',
    priority: 6,
    kind: 'interactive',
    title: 'An agent handles a workspace-access ticket',
    description: 'Hand a natural-language ticket to AI and follow its work in a separate application tab.',
    componentDescription: 'An interactive service desk dashboard, separate application tab, and floating browser-extension card from Henry’s PwC Browser Control work. Each ticket opens the application immediately. Visitors watch the agent translate the request into actions, pause, take over and edit, or approve the prepared change. The tickets also include a laptop request and an application sign-in. Resolved tickets retain the full action and decision history in the dashboard until the page is refreshed. The extension connects cloud reasoning to the operator’s existing browser.',
    narrativeGuidance: 'A high-priority hands-on example for Views about AI engineering, enterprise automation, or human control of agents. Readers can follow an access ticket from a service desk into a separate application, watch the agent fill the form, then pause, take over, or approve the change. It makes the division between cloud reasoning, browser actions, and operator decisions visible. Introduce the ticket and invite the reader to try an intervention before explaining what it shows. Use only when these actions help answer the reader’s question.',
    activationTopics: ['browser control', 'browser agent', 'human approval', 'service desk automation', 'Playwright'],
    route: '/ai-it-operations',
    anchor: 'browser-control-journey',
    topics: ['PwC', 'IT operations', 'browser control', 'browser agents', 'Playwright', 'LangGraph', 'human approval', 'Chrome extension'],
    allowedPresentation: 'interactive',
    component: 'ItOpsJourney'
  },
  {
    id: 'interactive.rag.flowchart-reconstruction',
    kind: 'interactive',
    title: 'Flowchart reconstruction demo',
    description: 'Compare a source flowchart with its reconstructed Mermaid representation and structured source.',
    componentDescription: 'An interactive comparison from the PwC RAG ingestion project that places the original flowchart beside its Mermaid reconstruction and the structured Mermaid source used for text-based retrieval.',
    narrativeGuidance: 'Use it when VLM-based document understanding, Mermaid, flowchart reconstruction, or diagram retrieval matters. Explain that the reconstruction preserves nodes, edges, labels, and branch structure in a representation that can be searched as text.',
    activationTopics: ['flowchart', 'Mermaid', 'visual document understanding', 'diagram retrieval'],
    route: '/ragflow-pipeline',
    anchor: 'mermaid',
    topics: ['RAG', 'flowcharts', 'Mermaid', 'document structure', 'PwC'],
    allowedPresentation: 'interactive',
    component: 'FlowchartReconstructionDemo'
  },
  {
    id: 'interactive.scrollytelling.chart-explorer',
    kind: 'interactive',
    title: 'Charts and the scenes that create them',
    description: 'Switch between figure types to see a scene schema and its rendered D3 result together.',
    componentDescription: 'A working explorer from Henry’s schema-driven scrollytelling engine. Visitors can switch figure types and inspect the small scene schema beside the output produced by the corresponding reusable D3 renderer.',
    narrativeGuidance: 'Establish that Henry built a scrolling web presentation whose figures appear alongside the explanation. Use this explorer to show how the same chart builders can serve another story with different text and data, then explain the relationship between a scene schema, the renderer registry, and the displayed figure when relevant.',
    activationTopics: ['scrollytelling engine', 'chart explorer', 'scene description', 'declarative charts', 'schema-driven rendering', 'renderer registry', 'D3 builders'],
    route: '/scrollytelling',
    anchor: 'describe-a-scene-once-then-let-the-engine-build-it',
    topics: ['scrollytelling', 'data visualization', 'D3', 'declarative charts'],
    allowedPresentation: 'interactive',
    component: 'ChartExplorer'
  },
  {
    id: 'embed.scrollytelling.live-deck',
    kind: 'embed',
    title: 'Live scrollytelling deck',
    description: 'The full scrolling presentation built with the engine.',
    componentDescription: 'The complete live scrollytelling deck produced with Henry’s engine, preserving its original scroll-driven sequence and presentation behavior.',
    narrativeGuidance: 'Use it when experiencing the finished storytelling system adds evidence beyond a technical description. Explain what the sequence demonstrates about the engine.',
    activationTopics: ['scrollytelling deck', 'live presentation', 'scrolling story'],
    route: '/scrollytelling',
    anchor: 'see-it-as-a-story',
    topics: ['scrollytelling', 'live demo', 'presentation'],
    allowedPresentation: 'embed',
    component: 'LiveDeckEmbed'
  },
  {
    id: 'interactive.travels.globe',
    kind: 'interactive',
    title: "Where I've Wandered",
    description: 'Explore the complete interactive globe of places Henry has visited.',
    componentDescription: 'The complete Travels globe from Henry’s site, including every location pin, place label, focus control, and location photograph as one interactive component. Visitors can drag, pinch, and select a place.',
    narrativeGuidance: 'Use it when geography, travel, or the pattern of places in Henry’s life directly answers the visitor. Explain which locations or regional pattern matters. Treat every pin as part of this single component and never turn the location list into a prose source.',
    activationTopics: ['travel', 'travels', 'travelled', 'traveled', 'places', 'destinations', 'cities', 'geography', 'globe', ...places.flatMap((place) => [place.en, place.label])],
    route: '/travels',
    anchor: '',
    topics: ['travel', 'travels', 'travelled', 'traveled', 'places', 'destinations', 'cities', 'geography', 'globe', ...places.flatMap((place) => [place.en, place.label])],
    allowedPresentation: 'interactive',
    component: 'TravelGlobe',
    componentFamily: 'travel-globe',
    media: travelMedia,
    variants: travelPlaceVariants,
    entity: {
      kind: 'interactive',
      id: 'travel-globe',
      places,
    },
  }
];

export function richBlockById(id) {
  for (const block of viewRichBlocks) {
    if (block.id === id) return block;
    const variant = (block.variants || []).find((item) => item.id === id);
    if (variant) {
      return {
        ...block,
        ...variant,
        variants: undefined,
        variantOf: block.id,
        componentFamily: block.componentFamily || block.id,
        entity: { ...(block.entity || {}), ...(variant.entity || {}) },
      };
    }
  }
  return undefined;
}
